import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Calendar, MapPin, Radio, Ticket, ArrowRight, Share2, ArrowLeft, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatZAR } from "../lib/api";
import { submitPayFast } from "../lib/payfast";
import { useAuth } from "../context/AuthContext";
import { EventPosterCarousel, TierSelector } from "./Events";

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let live = true;
    api.get(`/events/${id}`)
      .then(({ data }) => { if (live) setEvent(data); })
      .catch((err) => {
        if (!live) return;
        setError(err.response?.status === 404 ? "This event was not found." : "Could not load event.");
      })
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [id]);

  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/events/${id}` : "";
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — share it with your clients");
    } catch {
      toast.info(shareUrl);
    }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: event?.title, text: event?.description?.slice(0, 140), url: shareUrl });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const buy = async (tierId = null, quantity = 1) => {
    if (!user) {
      toast.error("Please sign in to purchase a ticket");
      navigate(`/login?next=/events/${id}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/payfast/event-ticket", {
        event_id: id, tier_id: tierId, quantity, origin_url: window.location.origin,
      });
      submitPayFast(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  if (loading) return <div className="py-32 text-center text-gray-500" data-testid="event-detail-loading">Loading…</div>;
  if (error || !event) {
    return (
      <div className="py-32 text-center max-w-md mx-auto px-4" data-testid="event-detail-error">
        <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-3">Not Available</div>
        <h1 className="font-display text-3xl text-[#1F3864] mb-3">{error || "Event not found."}</h1>
        <Link to="/events" className="text-[#C00000] font-semibold underline">Back to Events</Link>
      </div>
    );
  }

  const eventDate = new Date(event.event_date);
  const isPast = eventDate < new Date();
  const showTicketing = !isPast;

  return (
    <div data-testid={`event-detail-${id}`}>
      {/* Hero — poster carousel */}
      <section className="relative">
        <div className="aspect-[21/9] sm:aspect-[3/1] w-full bg-[#0B1526]">
          <EventPosterCarousel event={event} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1526] via-[#0B1526]/40 to-transparent" />
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-28 sm:-mt-40 relative pb-10">
          <Link to="/events" className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-white/80 hover:text-white mb-6" data-testid="back-to-events">
            <ArrowLeft className="w-3 h-3" /> All Events
          </Link>
          <h1 className="font-display text-4xl sm:text-6xl text-white leading-[1.05] max-w-4xl" data-testid="event-title">{event.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/85">
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />
              {eventDate.toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · {eventDate.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit", hour12: false })}
            </span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4" />{event.venue}</span>
            {event.is_live_stream && <span className="flex items-center gap-2 text-[#C00000] font-semibold"><Radio className="w-4 h-4" />Live Stream</span>}
          </div>
        </div>
      </section>

      {/* Share strip */}
      <section className="bg-[#F5F5F5] border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center gap-3">
          <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mr-2">Share this event</div>
          <div className="flex-1 min-w-[240px] px-3 py-2 bg-white border border-gray-300 text-xs text-gray-700 font-mono truncate" data-testid="event-share-url">
            {shareUrl}
          </div>
          <button onClick={copyLink} className="text-xs tracking-[0.15em] uppercase border border-[#1F3864] text-[#1F3864] px-4 py-2 hover:bg-[#1F3864] hover:text-white transition-colors" data-testid="event-copy-link">
            Copy Link
          </button>
          <button onClick={nativeShare} className="text-xs tracking-[0.15em] uppercase border border-[#C00000] text-[#C00000] px-4 py-2 hover:bg-[#C00000] hover:text-white transition-colors flex items-center gap-2" data-testid="event-native-share">
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>
      </section>

      {/* Body */}
      <section className="py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">About this event</div>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line text-lg">{event.description}</p>
            {event.photos_published && (
              <Link
                to={`/events/${id}/photos`}
                className="mt-8 inline-flex items-center gap-3 text-[#1F3864] font-semibold hover:text-[#C00000] transition-colors"
                data-testid="event-detail-photos-link"
              >
                <ImageIcon className="w-4 h-4" /> View the Photo Gallery <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>

          <aside className="lg:col-span-5">
            <div className="bg-white border border-gray-200 p-6 lg:p-8 lg:sticky lg:top-24">
              {showTicketing ? (
                (!event.general_admission && event.tiers?.length > 0) ? (
                  <>
                    <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-4">Get Tickets</div>
                    <TierSelector event={event} onBuy={(tierId, qty) => buy(tierId, qty)} busy={busy} />
                  </>
                ) : (
                  <div>
                    <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Ticket</div>
                    <div className="font-display text-3xl text-[#1F3864] mb-6">
                      {event.ticket_price > 0 ? formatZAR(event.ticket_price) : "Free RSVP"}
                    </div>
                    {event.ticket_price > 0 ? (
                      <button onClick={() => buy(null, 1)} disabled={busy} className="btn-primary-red w-full justify-center disabled:opacity-60" data-testid={`action-buy-ticket-${id}`}>
                        <Ticket className="w-4 h-4" /> {busy ? "Loading…" : "Buy Ticket / Stream"}
                      </button>
                    ) : (
                      <button onClick={() => toast.success("RSVP received — see you there!")} className="btn-primary-navy w-full justify-center" data-testid={`action-rsvp-${id}`}>
                        <ArrowRight className="w-4 h-4" /> RSVP
                      </button>
                    )}
                  </div>
                )
              ) : (
                <div className="text-center py-6">
                  <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">This event has passed</div>
                  {event.photos_published ? (
                    <Link to={`/events/${id}/photos`} className="btn-primary-red inline-flex" data-testid="past-event-photos">
                      <ImageIcon className="w-4 h-4" /> View Photos
                    </Link>
                  ) : (
                    <div className="text-sm text-gray-500 mt-2">Check back soon for photos & recordings.</div>
                  )}
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
